// เหตุการณ์เฉพาะตัวของนกที่มีชื่อเสียง (ดู signature.ts)

import type { GameEvent } from "../../engine/types";
import { sig } from "./signature";

const ADULT = ["adult", "old"] as const;

export const SIGNATURE_BIRD_EVENTS: readonly GameEvent[] = [
  sig("Buceros bicornis", "hornbill_sealed_nest", {
    stageTags: ADULT,
    text: "คู่ของคุณเจอโพรงไม้สูงที่เหมาะจะทำรัง ต้องตัดสินใจว่าใครจะเข้าไปอยู่ในโพรง",
    fact: "นกเงือกตัวเมียปิดปากโพรงขังตัวเองไว้ข้างในระหว่างกกไข่และเลี้ยงลูก ตัวผู้ต้องหาผลไม้มาป้อนผ่านช่องแคบๆ อยู่หลายเดือน",
    choices: [
      {
        label: "ช่วยกันปิดปากโพรงด้วยดินและมูล",
        outcomes: [
          { chance: 0.8, text: "ปากโพรงเหลือแค่ช่องแคบพอให้ปากลอดผ่าน งูและกระรอกเข้ามาไม่ได้", effects: { safety: 15, fullness: -10 } },
          { chance: 0.2, text: "ฝนตกหนักจนดินที่ปิดไว้พัง ต้องปิดใหม่ทั้งหมด", effects: { fullness: -20 } },
        ],
      },
      { label: "หาโพรงที่ดีกว่านี้", outcomes: [{ chance: 1, text: "ต้นไม้ใหญ่ที่มีโพรงเหลือน้อยลงทุกปี คุณบินหาอยู่นาน", effects: { fullness: -15, move: "near" } }] },
    ],
  }),
  sig("Passer domesticus", "sparrow_humans", {
    text: "คุณเจอร้านขายข้าวที่มีเมล็ดข้าวหกเกลื่อนพื้น คนเดินผ่านไปมาไม่ขาดสาย",
    fact: "นกกระจอกใหญ่อาศัยอยู่ใกล้มนุษย์มาหลายพันปี และแพร่ไปทั่วโลกพร้อมการตั้งถิ่นฐานของคน แทบไม่พบในป่าที่ไม่มีคนอยู่",
    choices: [
      { label: "ลงไปจิกข้าวพร้อมฝูง", outcomes: [{ chance: 1, text: "ฝูงช่วยกันระวังภัย คุณได้กินเต็มที่", effects: { fullness: 25 } }] },
      {
        label: "หาแมลงในสวนหลังบ้านแทน",
        outcomes: [
          { chance: 0.7, text: "หนอนตัวอ้วนอยู่ใต้ใบผัก", effects: { fullness: 20 } },
          { chance: 0.3, text: "แมวบ้านซุ่มอยู่ในสวน", effects: { health: -25, cause: "predator" } },
        ],
      },
    ],
  }),
  sig("Passer montanus", "tree_sparrow_campaign", {
    text: "ชาวนาไล่ตีกลองเสียงดังรอบนาข้าวไม่ยอมหยุด ฝูงนกกระจอกไม่มีที่ให้เกาะพัก",
    fact: "ปี 1958 จีนรณรงค์กำจัดนกกระจอกบ้านเพราะกินเมล็ดข้าว คนตีกลองไล่จนนกบินจนหมดแรงตกตาย ปีต่อมาแมลงศัตรูพืชที่นกเคยกินระบาดหนักจนพืชผลเสียหาย",
    choices: [
      {
        label: "บินหนีไปไกลๆ",
        outcomes: [
          { chance: 0.7, text: "คุณหนีพ้นเขตที่มีเสียงกลองได้", effects: { fullness: -20, move: "far" } },
          { chance: 0.3, text: "คุณบินจนหมดแรงร่วงลงกลางทุ่ง", effects: { health: -35, cause: "human" } },
        ],
      },
      { label: "แอบหลบในชายคาบ้าน", outcomes: [{ chance: 1, text: "ชายคามืดและเงียบ คุณรอจนเสียงกลองหยุด", effects: { fullness: -10, safety: 5 } }] },
    ],
  }),
  sig("Hirundo rustica", "swallow_mud_nest", {
    stageTags: ADULT,
    text: "คู่ของคุณเลือกใต้ชายคาบ้านคนเป็นที่ทำรัง ต้องขนโคลนมาปั้นรังทีละคำ",
    fact: "นกนางแอ่นบ้านปั้นรังจากก้อนโคลนเล็กๆ หลายร้อยก้อนผสมเส้นหญ้า และมักกลับมาใช้ที่ทำรังเดิมทุกปี",
    choices: [
      { label: "ขนโคลนจากริมคูน้ำ", outcomes: [{ chance: 1, text: "รังรูปถ้วยเสร็จในไม่กี่วัน แข็งแรงพอจะใช้ได้หลายปี", effects: { fullness: -10, safety: 10 } }] },
      { label: "ซ่อมรังเก่าที่ยังเหลืออยู่", outcomes: [{ chance: 1, text: "ประหยัดแรงไปได้มาก มีเวลาจับแมลงเพิ่ม", effects: { fullness: 15 } }] },
    ],
  }),
  sig("Gallus gallus", "junglefowl_tidbit", {
    stageTags: ADULT,
    text: "คุณคุ้ยใบไม้เจอปลวกกลุ่มใหญ่ ตัวเมียหลายตัวหากินอยู่ไม่ไกล",
    fact: "ไก่ป่าตัวผู้ขันตอนเช้าตรู่เพื่อประกาศอาณาเขต และส่งเสียงเรียกพร้อมจิกอาหารขึ้นลงให้ตัวเมียดูเมื่อเจออาหารดีๆ ไก่บ้านทั่วโลกสืบเชื้อสายมาจากไก่ป่า",
    choices: [
      { label: "ส่งเสียงเรียกตัวเมียมากิน", outcomes: [{ chance: 1, text: "ตัวเมียเดินมาหาคุณ ประทับใจในอาหารที่คุณเจอ", effects: { fullness: 5, addFlags: ["has_mate"] } }] },
      { label: "กินเองให้อิ่ม", outcomes: [{ chance: 1, text: "ปลวกเต็มปาก อิ่มไปทั้งวัน", effects: { fullness: 25 } }] },
    ],
  }),
  sig("Tyto alba", "barn_owl_silent", {
    stageTags: ADULT,
    text: "คืนเดือนมืด คุณได้ยินเสียงหนูวิ่งอยู่ใต้กองฟางในโรงนา",
    fact: "นกแสกมีขนขอบปีกหยักเป็นซี่นุ่มคล้ายหวี ทำให้บินได้เงียบจนเหยื่อไม่ได้ยินเสียงเลย",
    choices: [
      { label: "ร่อนลงอย่างเงียบเชียบ", outcomes: [{ chance: 1, text: "หนูไม่รู้ตัวจนกรงเล็บของคุณจับมันไว้แล้ว", effects: { fullness: 30 } }] },
      { label: "เกาะคานรอให้มันออกมาเอง", outcomes: [{ chance: 1, text: "หนูออกมาหลายตัว คุณเลือกจับตัวที่อ้วนที่สุด", effects: { fullness: 25, safety: 5 } }] },
    ],
  }),
  sig("Spilornis cheela", "serpent_eagle_snake", {
    stageTags: ADULT,
    text: "จากกิ่งไม้สูง คุณเห็นงูเขียวตัวหนึ่งเลื้อยอยู่บนพื้นป่าโล่ง",
    fact: "เหยี่ยวรุ้งเกาะนิ่งๆ ในป่ารอจังหวะโฉบงู และร้องเสียงแหลมดังขณะร่อนวนเหนือยอดไม้ ขาของมันมีเกล็ดหนาช่วยกันเขี้ยวงู",
    choices: [
      {
        label: "โฉบลงไปจับ",
        outcomes: [
          { chance: 0.75, text: "กรงเล็บจับหัวงูไว้แน่น คุณกินมันทั้งตัว", effects: { fullness: 35 } },
          { chance: 0.25, text: "งูหันมาฉกโดนขา แต่เกล็ดหนาช่วยไว้", effects: { fullness: 10, health: -10 } },
        ],
      },
      { label: "ร่อนวนหาตัวที่เล็กกว่า", outcomes: [{ chance: 1, text: "คุณเจอกิ้งก่าตัวใหญ่แทน", effects: { fullness: 20 } }] },
    ],
  }),
  sig("Haliastur indus", "brahminy_kite_harbour", {
    text: "เรือประมงเพิ่งเข้าท่า ชาวประมงโยนเศษปลาลงน้ำ",
    fact: "เหยี่ยวแดงชอบหากินตามชายฝั่งและท่าเรือ ใช้กรงเล็บเก็บปลาตายและเศษอาหารจากผิวน้ำโดยแทบไม่เปียก",
    choices: [
      { label: "โฉบเก็บเศษปลาจากผิวน้ำ", outcomes: [{ chance: 1, text: "อาหารง่ายๆ ไม่ต้องล่าเลย", effects: { fullness: 25 } }] },
      {
        label: "แย่งปลาจากนกตัวอื่น",
        outcomes: [
          { chance: 0.5, text: "นกกาน้ำปล่อยปลาตกลงมา คุณรับไว้กลางอากาศ", effects: { fullness: 30 } },
          { chance: 0.5, text: "นกนางนวลรุมไล่จิกคุณ", effects: { health: -10 } },
        ],
      },
    ],
  }),
  sig("Alcedo atthis", "kingfisher_dive", {
    text: "คุณเกาะกิ่งไม้เหนือลำธาร ปลาตัวเล็กว่ายผ่านมาในน้ำใส",
    fact: "นกกระเต็นพุ่งหัวดำลงน้ำจับปลา โดยมีเยื่อตาโปร่งใสปิดตาไว้ตอนลงน้ำ แล้วฟาดปลากับกิ่งไม้ก่อนกลืนหัวเข้าก่อน",
    choices: [
      {
        label: "พุ่งดำลงไป",
        outcomes: [
          { chance: 0.7, text: "คุณโผล่พ้นน้ำพร้อมปลาในปาก", effects: { fullness: 25 } },
          { chance: 0.3, text: "พลาด ขนเปียกจนต้องตากแดดนาน", effects: { fullness: -5 } },
        ],
      },
      { label: "บินโฉบเหนือน้ำรอปลาตัวใหญ่", outcomes: [{ chance: 1, text: "คุณกระพือปีกนิ่งกลางอากาศ แล้วเลือกปลาตัวที่อ้วนที่สุด", effects: { fullness: 20, safety: -5 } }] },
    ],
  }),
  sig("Anastomus oscitans", "openbill_snails", {
    text: "นาข้าวเพิ่งได้น้ำ หอยเชอรี่คลานเต็มคันนา",
    fact: "นกปากห่างมีปากที่ปิดไม่สนิท เป็นช่องตรงกลาง ช่วยคีบหอยโข่งและหอยเชอรี่ แล้วตัดกล้ามเนื้อให้ฝาเปิดออก",
    choices: [
      { label: "ลงนาไปกินหอยเชอรี่", outcomes: [{ chance: 1, text: "หอยเชอรี่มีไม่อั้น ชาวนาก็ชอบเพราะคุณช่วยกำจัดศัตรูข้าว", effects: { fullness: 35 } }] },
      { label: "ไปหาหอยโข่งในหนองน้ำ", outcomes: [{ chance: 1, text: "หอยโข่งน้อยกว่า แต่หนองน้ำเงียบสงบ", effects: { fullness: 20, safety: 5 } }] },
    ],
  }),
  sig("Merops philippinus", "bee_eater_sting", {
    text: "คุณจับผึ้งได้ตัวหนึ่งกลางอากาศ เหล็กในยังยื่นออกมา",
    fact: "นกจาบคาจับผึ้งและต่อกลางอากาศ แล้วนำไปฟาดกับกิ่งไม้ถูไปมาเพื่อรีดพิษและเหล็กในออกก่อนกิน",
    choices: [
      { label: "ฟาดกับกิ่งไม้ให้เหล็กในหลุด", outcomes: [{ chance: 1, text: "คุณกินผึ้งได้อย่างปลอดภัย แล้วออกไปจับตัวต่อไป", effects: { fullness: 25 } }] },
      { label: "กลืนทันที", outcomes: [{ chance: 1, text: "เหล็กในต่อยคอคุณระหว่างกลืน", effects: { fullness: 10, health: -15 } }] },
    ],
  }),
  sig("Dicrurus macrocercus", "drongo_mobbing", {
    stageTags: ADULT,
    text: "กาตัวใหญ่บินวนเข้ามาใกล้รังของคุณ",
    fact: "นกแซงแซวหางปลากล้าไล่โจมตีเหยี่ยวและกาที่บินเข้าใกล้รัง นกเล็กชนิดอื่นจึงชอบมาทำรังใกล้ๆ เพื่อให้ช่วยคุ้มกัน",
    choices: [
      {
        label: "พุ่งเข้าไล่จิก",
        outcomes: [
          { chance: 0.8, text: "กาตกใจรีบบินหนี นกเล็กแถวนั้นร้องเฮกันลั่น", effects: { safety: 15 } },
          { chance: 0.2, text: "กาหันมาจิกสวน ขนหางคุณหลุดไปเส้นหนึ่ง", effects: { health: -10 } },
        ],
      },
      { label: "เฝ้ารังเงียบๆ", outcomes: [{ chance: 1, text: "กาบินผ่านไปโดยไม่สนใจรังของคุณ", effects: { safety: 5 } }] },
    ],
  }),
  sig("Copsychus saularis", "magpie_robin_song", {
    stageTags: ADULT,
    text: "เช้านี้นกกางเขนตัวผู้อีกตัวมาร้องเพลงบนเสาไฟหน้าบ้านที่เป็นอาณาเขตของคุณ",
    fact: "นกกางเขนบ้านร้องเพลงได้หลายทำนอง เลียนเสียงนกชนิดอื่นได้ และเป็นนกประจำชาติของบังกลาเทศ",
    choices: [
      { label: "ร้องเพลงแข่งให้ดังกว่า", outcomes: [{ chance: 1, text: "เพลงของคุณไพเราะและดังกว่า ตัวผู้อีกตัวบินจากไป", effects: { safety: 10, addFlags: ["has_mate"] } }] },
      {
        label: "บินเข้าไปไล่",
        outcomes: [
          { chance: 0.6, text: "มันยอมถอยไป", effects: { safety: 5 } },
          { chance: 0.4, text: "ตีกันจนขนกระจาย", effects: { health: -15, cause: "accident" } },
        ],
      },
    ],
  }),
  sig("Acridotheres tristis", "myna_invader", {
    text: "คุณเห็นโพรงรังของนกท้องถิ่นในต้นไม้ใหญ่ เจ้าของกำลังออกไปหาอาหาร",
    fact: "นกเอี้ยงสาริกาถูกนำไปปล่อยในหลายประเทศเพื่อกำจัดแมลง แต่กลับกลายเป็นนกรุกรานที่แย่งโพรงรังของนกท้องถิ่น",
    choices: [
      {
        label: "ยึดโพรงนั้นเป็นของตัวเอง",
        outcomes: [
          { chance: 0.7, text: "คุณได้โพรงรังชั้นดี", effects: { safety: 15 } },
          { chance: 0.3, text: "เจ้าของกลับมาพร้อมคู่ คุณถูกรุมจิก", effects: { health: -15 } },
        ],
      },
      { label: "ทำรังใต้หลังคาบ้านคนแทน", outcomes: [{ chance: 1, text: "หลังคาบ้านก็อบอุ่นไม่แพ้กัน", effects: { safety: 10 } }] },
    ],
  }),
  sig("Pandion haliaetus", "osprey_fish", {
    stageTags: ADULT,
    text: "ปลาตัวใหญ่ว่ายขึ้นมาใกล้ผิวน้ำ คุณกระพือปีกนิ่งอยู่เหนือมัน",
    fact: "เหยี่ยวออสเปรหมุนนิ้วเท้าด้านนอกไปข้างหลังได้ ช่วยจับปลาลื่นให้แน่น และหันหัวปลาไปข้างหน้าขณะบินเพื่อลดแรงต้านลม",
    choices: [
      {
        label: "พุ่งลงจับด้วยกรงเล็บ",
        outcomes: [
          { chance: 0.7, text: "คุณยกปลาขึ้นจากน้ำ หันหัวปลาไปข้างหน้าแล้วบินกลับรัง", effects: { fullness: 35 } },
          { chance: 0.3, text: "ปลาหนักเกินไป คุณต้องปล่อยมันกลางน้ำ", effects: { fullness: -10 } },
        ],
      },
      { label: "รอปลาที่เล็กกว่านี้", outcomes: [{ chance: 1, text: "ปลาขนาดพอดีว่ายมา คุณจับได้ง่ายๆ", effects: { fullness: 20 } }] },
    ],
  }),
  sig("Haliaeetus leucocephalus", "bald_eagle_comeback", {
    stageTags: ADULT,
    text: "คุณกับคู่สร้างรังบนต้นสนสูง รังของคุณใหญ่ขึ้นทุกปีที่กลับมาใช้",
    fact: "นกอินทรีหัวขาวเกือบสูญพันธุ์จากยาฆ่าแมลง DDT ที่ทำให้เปลือกไข่บางจนแตก หลังสหรัฐอเมริกาเลิกใช้ DDT จำนวนก็กลับมาเพิ่มขึ้นจนพ้นสถานะใกล้สูญพันธุ์",
    choices: [
      { label: "เสริมรังด้วยกิ่งไม้ใหม่", outcomes: [{ chance: 1, text: "รังของคุณกว้างกว่า 2 เมตรแล้ว", effects: { safety: 10, fullness: -10 } }] },
      { label: "ไปจับปลาแซลมอนในแม่น้ำ", outcomes: [{ chance: 1, text: "ปลาว่ายขึ้นมาวางไข่เต็มแม่น้ำ", effects: { fullness: 30 } }] },
    ],
  }),
  sig("Aptenodytes forsteri", "emperor_huddle", {
    stageTags: ADULT,
    text: "พายุหิมะพัดกระหน่ำในคืนขั้วโลกที่ไม่มีพระอาทิตย์ คุณยืนกกไข่ไว้บนหลังเท้า",
    fact: "เพนกวินจักรพรรดิตัวผู้กกไข่บนหลังเท้าตลอดฤดูหนาวขั้วโลกราว 2 เดือนโดยไม่กินอะไรเลย และเบียดกันเป็นกลุ่มใหญ่ผลัดกันเข้าไปอยู่ตรงกลางเพื่อกันหนาว",
    choices: [
      { label: "เบียดเข้ากลุ่มผลัดกันอยู่ตรงกลาง", outcomes: [{ chance: 1, text: "กลุ่มค่อยๆ หมุนวน ทุกตัวได้อุ่นเท่าๆ กัน", effects: { health: 5, fullness: -15 } }] },
      {
        label: "ยืนอยู่ขอบกลุ่มที่ลมแรง",
        outcomes: [
          { chance: 0.5, text: "หนาวจนตัวสั่น แต่ไข่ยังปลอดภัย", effects: { health: -10, fullness: -20 } },
          { chance: 0.5, text: "ลมแรงจนคุณเสียหลัก เกือบทำไข่หลุดจากเท้า", effects: { health: -20, fullness: -20, cause: "disaster" } },
        ],
      },
    ],
  }),
  sig("Bubo virginianus", "great_horned_skunk", {
    stageTags: ADULT,
    text: "สกังก์ตัวหนึ่งเดินอยู่ในทุ่ง สัตว์อื่นต่างหลีกทางให้",
    fact: "นกเค้าใหญ่อเมริกาเป็นนักล่าไม่กี่ชนิดที่ล่าสกังก์เป็นประจำ เพราะนกส่วนใหญ่ได้กลิ่นไม่ค่อยดี",
    choices: [
      { label: "โฉบลงจับ", outcomes: [{ chance: 1, text: "กลิ่นฉุนไม่ได้ทำให้คุณสะทกสะท้าน คุณได้อาหารมื้อใหญ่", effects: { fullness: 35 } }] },
      { label: "รอกระต่ายแทน", outcomes: [{ chance: 1, text: "กระต่ายโผล่ออกมาจากโพรงตอนรุ่งสาง", effects: { fullness: 25 } }] },
    ],
  }),
  sig("Polemaetus bellicosus", "martial_eagle_sight", {
    stageTags: ADULT,
    text: "คุณร่อนสูงเหนือทุ่งหญ้าสะวันนา มองเห็นอะไรบางอย่างขยับอยู่ไกลลิบ",
    fact: "นกอินทรีมาร์เชียลเป็นอินทรีที่ใหญ่ที่สุดในแอฟริกา สายตาคมจนมองเห็นเหยื่อจากที่สูงได้ไกลหลายกิโลเมตร",
    choices: [
      {
        label: "ดิ่งลงจากที่สูง",
        outcomes: [
          { chance: 0.7, text: "เหยื่อไม่ทันเห็นคุณที่ดิ่งลงมาจากฟ้า", effects: { fullness: 35 } },
          { chance: 0.3, text: "เป็นลูกอิมพาลาที่แม่อยู่ใกล้ๆ คุณถูกไล่จนต้องบินหนี", effects: { fullness: -5 } },
        ],
      },
      { label: "ร่อนหาต่อ", outcomes: [{ chance: 1, text: "คุณเจอตะกวดตัวใหญ่ข้างจอมปลวก", effects: { fullness: 25 } }] },
    ],
  }),
  sig("Egretta garzetta", "egret_foot_stir", {
    text: "น้ำในนาข้าวขุ่น มองไม่เห็นปลาที่ซ่อนอยู่",
    fact: "นกยางเปียใช้เท้าสีเหลืองกวนโคลนให้ปลาและกุ้งตกใจออกมาจากที่ซ่อน แล้วจิกจับอย่างรวดเร็ว",
    choices: [
      { label: "ใช้เท้ากวนโคลน", outcomes: [{ chance: 1, text: "ปลาซิวและกุ้งฝอยตกใจว่ายออกมา คุณจิกได้หลายตัว", effects: { fullness: 25 } }] },
      { label: "เดินตามควายที่ไถนา", outcomes: [{ chance: 1, text: "ควายเดินไปตรงไหน แมลงก็กระโดดหนีออกมาให้จับ", effects: { fullness: 20, safety: 5 } }] },
    ],
  }),
  sig("Spilopelia chinensis", "dove_crop_milk", {
    stageTags: ADULT,
    text: "ลูกนกในรังร้องขออาหาร แต่เมล็ดพืชแข็งเกินไปสำหรับพวกมัน",
    fact: "นกเขาและนกพิราบเลี้ยงลูกอ่อนด้วยน้ำนมพิราบที่สร้างจากกระเพาะพัก และพ่อแม่สร้างได้ทั้งตัวผู้และตัวเมีย",
    choices: [
      { label: "ป้อนน้ำนมพิราบ", outcomes: [{ chance: 1, text: "ลูกนกโตเร็วทันตา แต่คุณใช้พลังงานไปมาก", effects: { fullness: -15, safety: 5 } }] },
      { label: "ออกไปหาเมล็ดหญ้าอ่อนๆ", outcomes: [{ chance: 1, text: "เมล็ดอ่อนพอให้ลูกที่โตขึ้นมาแล้วกินได้", effects: { fullness: 10 } }] },
    ],
  }),
];
